import { MockProperty } from "@/data/mockProperties";

export interface ScoreBreakdown {
  category: string;
  score: number;
  maxScore: number;
  weight: number;
  weightedScore: number;
  details: string;
  status: "excellent" | "good" | "fair" | "poor";
}

export interface Section8DealScore {
  totalScore: number;
  maxScore: number;
  percentage: number;
  grade: "A+" | "A" | "B+" | "B" | "C+" | "C" | "D" | "F";
  recommendation: "Strong Buy" | "Buy" | "Hold" | "Avoid";
  breakdown: ScoreBreakdown[];
}

const getStatus = (percentage: number): "excellent" | "good" | "fair" | "poor" => {
  if (percentage >= 80) return "excellent";
  if (percentage >= 60) return "good";
  if (percentage >= 40) return "fair";
  return "poor";
};

const getGrade = (percentage: number): Section8DealScore["grade"] => {
  if (percentage >= 95) return "A+";
  if (percentage >= 85) return "A";
  if (percentage >= 80) return "B+";
  if (percentage >= 70) return "B";
  if (percentage >= 65) return "C+";
  if (percentage >= 55) return "C";
  if (percentage >= 45) return "D";
  return "F";
};

const getRecommendation = (percentage: number): Section8DealScore["recommendation"] => {
  if (percentage >= 80) return "Strong Buy";
  if (percentage >= 65) return "Buy";
  if (percentage >= 50) return "Hold";
  return "Avoid";
};

export function calculateSection8DealScore(
  property: MockProperty,
  calculatedMonthlyHAP?: number,
  utilityAllowance?: number
): Section8DealScore {
  const breakdown: ScoreBreakdown[] = [];
  
  // 1. FMR Coverage Score (Weight: 20%)
  // How well does the FMR cover the estimated rent?
  const fmrCoverageRatio = property.section8Fmr / property.estimatedRent;
  let fmrScore = 0;
  let fmrDetails = "";
  
  if (fmrCoverageRatio >= 1.1) {
    fmrScore = 10;
    fmrDetails = `FMR ($${property.section8Fmr.toLocaleString()}) exceeds rent by ${((fmrCoverageRatio - 1) * 100).toFixed(0)}%`;
  } else if (fmrCoverageRatio >= 1.0) {
    fmrScore = 8;
    fmrDetails = `FMR covers 100% of rent`;
  } else if (fmrCoverageRatio >= 0.95) {
    fmrScore = 6;
    fmrDetails = `FMR covers ${(fmrCoverageRatio * 100).toFixed(0)}% of rent - minimal gap`;
  } else if (fmrCoverageRatio >= 0.85) {
    fmrScore = 4;
    fmrDetails = `FMR covers ${(fmrCoverageRatio * 100).toFixed(0)}% of rent - consider rent reduction`;
  } else {
    fmrScore = 2;
    fmrDetails = `FMR only covers ${(fmrCoverageRatio * 100).toFixed(0)}% of rent - significant gap`;
  }
  
  breakdown.push({
    category: "FMR Coverage",
    score: fmrScore,
    maxScore: 10,
    weight: 20,
    weightedScore: (fmrScore / 10) * 20,
    details: fmrDetails,
    status: getStatus((fmrScore / 10) * 100),
  });

  // 2. Cash Flow Score (Weight: 25%)
  // Based on cash-on-cash return
  let cashFlowScore = 0;
  let cashFlowDetails = "";
  
  if (property.cashOnCash >= 15) {
    cashFlowScore = 10;
    cashFlowDetails = `Exceptional ${property.cashOnCash.toFixed(1)}% CoC return`;
  } else if (property.cashOnCash >= 12) {
    cashFlowScore = 8;
    cashFlowDetails = `Strong ${property.cashOnCash.toFixed(1)}% CoC return`;
  } else if (property.cashOnCash >= 10) {
    cashFlowScore = 6;
    cashFlowDetails = `Good ${property.cashOnCash.toFixed(1)}% CoC return`;
  } else if (property.cashOnCash >= 8) {
    cashFlowScore = 4;
    cashFlowDetails = `Acceptable ${property.cashOnCash.toFixed(1)}% CoC return`;
  } else {
    cashFlowScore = 2;
    cashFlowDetails = `Low ${property.cashOnCash.toFixed(1)}% CoC return`;
  }
  
  breakdown.push({
    category: "Cash Flow Potential",
    score: cashFlowScore,
    maxScore: 10,
    weight: 25,
    weightedScore: (cashFlowScore / 10) * 25,
    details: cashFlowDetails,
    status: getStatus((cashFlowScore / 10) * 100),
  });

  // 3. Vacancy Risk Score (Weight: 15%)
  // Lower vacancy rate = higher score
  let vacancyScore = 0;
  let vacancyDetails = "";
  
  if (property.vacancyRate <= 3) {
    vacancyScore = 10;
    vacancyDetails = `Very low ${property.vacancyRate}% vacancy rate - high demand area`;
  } else if (property.vacancyRate <= 5) {
    vacancyScore = 8;
    vacancyDetails = `Low ${property.vacancyRate}% vacancy rate - stable market`;
  } else if (property.vacancyRate <= 7) {
    vacancyScore = 6;
    vacancyDetails = `Moderate ${property.vacancyRate}% vacancy rate`;
  } else if (property.vacancyRate <= 10) {
    vacancyScore = 4;
    vacancyDetails = `Elevated ${property.vacancyRate}% vacancy rate - some risk`;
  } else {
    vacancyScore = 2;
    vacancyDetails = `High ${property.vacancyRate}% vacancy rate - significant risk`;
  }
  
  breakdown.push({
    category: "Vacancy Risk",
    score: vacancyScore,
    maxScore: 10,
    weight: 15,
    weightedScore: (vacancyScore / 10) * 15,
    details: vacancyDetails,
    status: getStatus((vacancyScore / 10) * 100),
  });

  // 4. Cap Rate Score (Weight: 15%)
  let capRateScore = 0;
  let capRateDetails = "";
  
  if (property.capRate >= 10) {
    capRateScore = 10;
    capRateDetails = `Excellent ${property.capRate.toFixed(1)}% cap rate`;
  } else if (property.capRate >= 8) {
    capRateScore = 8;
    capRateDetails = `Strong ${property.capRate.toFixed(1)}% cap rate`;
  } else if (property.capRate >= 7) {
    capRateScore = 6;
    capRateDetails = `Good ${property.capRate.toFixed(1)}% cap rate`;
  } else if (property.capRate >= 6) {
    capRateScore = 4;
    capRateDetails = `Average ${property.capRate.toFixed(1)}% cap rate`;
  } else {
    capRateScore = 2;
    capRateDetails = `Below average ${property.capRate.toFixed(1)}% cap rate`;
  }
  
  breakdown.push({
    category: "Cap Rate",
    score: capRateScore,
    maxScore: 10,
    weight: 15,
    weightedScore: (capRateScore / 10) * 15,
    details: capRateDetails,
    status: getStatus((capRateScore / 10) * 100),
  });

  // 5. Value-Add Opportunity (Weight: 10%)
  // Based on equity estimate and ARV vs price
  const equityRatio = property.equityEstimate / property.price;
  let valueAddScore = 0;
  let valueAddDetails = "";
  
  if (equityRatio >= 0.3) {
    valueAddScore = 10;
    valueAddDetails = `$${property.equityEstimate.toLocaleString()} built-in equity (${(equityRatio * 100).toFixed(0)}% of price)`;
  } else if (equityRatio >= 0.2) {
    valueAddScore = 8;
    valueAddDetails = `$${property.equityEstimate.toLocaleString()} equity potential (${(equityRatio * 100).toFixed(0)}% of price)`;
  } else if (equityRatio >= 0.15) {
    valueAddScore = 6;
    valueAddDetails = `$${property.equityEstimate.toLocaleString()} moderate equity (${(equityRatio * 100).toFixed(0)}% of price)`;
  } else if (equityRatio >= 0.1) {
    valueAddScore = 4;
    valueAddDetails = `$${property.equityEstimate.toLocaleString()} limited equity (${(equityRatio * 100).toFixed(0)}% of price)`;
  } else {
    valueAddScore = 2;
    valueAddDetails = `Minimal built-in equity opportunity`;
  }
  
  breakdown.push({
    category: "Value-Add Potential",
    score: valueAddScore,
    maxScore: 10,
    weight: 10,
    weightedScore: (valueAddScore / 10) * 10,
    details: valueAddDetails,
    status: getStatus((valueAddScore / 10) * 100),
  });

  // 6. Motivated Seller / Deal Indicators (Weight: 10%)
  let dealIndicatorScore = 0;
  let dealIndicatorDetails: string[] = [];
  
  if (property.motivatedSeller) {
    dealIndicatorScore += 3;
    dealIndicatorDetails.push("Motivated seller");
  }
  if (property.belowMarketValue) {
    dealIndicatorScore += 3;
    dealIndicatorDetails.push("Below market value");
  }
  if (property.priceReduction && property.priceReduction > 0) {
    dealIndicatorScore += 2;
    dealIndicatorDetails.push(`$${property.priceReduction.toLocaleString()} price reduction`);
  }
  if (property.daysOnMarket > 60) {
    dealIndicatorScore += 2;
    dealIndicatorDetails.push(`${property.daysOnMarket} days on market`);
  }
  
  dealIndicatorScore = Math.min(dealIndicatorScore, 10);
  
  breakdown.push({
    category: "Deal Indicators",
    score: dealIndicatorScore,
    maxScore: 10,
    weight: 10,
    weightedScore: (dealIndicatorScore / 10) * 10,
    details: dealIndicatorDetails.length > 0 ? dealIndicatorDetails.join(", ") : "No special deal indicators",
    status: getStatus((dealIndicatorScore / 10) * 100),
  });

  // 7. Property Condition Score (Weight: 5%)
  // Based on repair estimate vs price ratio
  const repairRatio = property.repairEstimate / property.price;
  let conditionScore = 0;
  let conditionDetails = "";
  
  if (repairRatio <= 0.05) {
    conditionScore = 10;
    conditionDetails = `Minimal repairs ($${property.repairEstimate.toLocaleString()}) - move-in ready`;
  } else if (repairRatio <= 0.1) {
    conditionScore = 8;
    conditionDetails = `Light repairs ($${property.repairEstimate.toLocaleString()}) - cosmetic updates`;
  } else if (repairRatio <= 0.15) {
    conditionScore = 6;
    conditionDetails = `Moderate repairs ($${property.repairEstimate.toLocaleString()}) needed`;
  } else if (repairRatio <= 0.25) {
    conditionScore = 4;
    conditionDetails = `Significant repairs ($${property.repairEstimate.toLocaleString()}) required`;
  } else {
    conditionScore = 2;
    conditionDetails = `Major rehab ($${property.repairEstimate.toLocaleString()}) - high execution risk`;
  }
  
  breakdown.push({
    category: "Property Condition",
    score: conditionScore,
    maxScore: 10,
    weight: 5,
    weightedScore: (conditionScore / 10) * 5,
    details: conditionDetails,
    status: getStatus((conditionScore / 10) * 100),
  });

  // Calculate total weighted score
  const totalWeightedScore = breakdown.reduce((sum, item) => sum + item.weightedScore, 0);
  const maxScore = 100;
  const percentage = totalWeightedScore;
  
  return {
    totalScore: Math.round(totalWeightedScore),
    maxScore,
    percentage: Math.round(percentage),
    grade: getGrade(percentage),
    recommendation: getRecommendation(percentage),
    breakdown,
  };
}
